import React, { useState, useMemo } from 'react';
import { RawTrade } from '../function/type'; // Kiểm tra lại path import

interface TradingCalendarProps {
  rawTrades?: RawTrade[];
  onDateClick: (formattedDate: string, tradesOfDay: RawTrade[]) => void;
}

// Hàm hỗ trợ format ngày local thành "YYYY-MM-DD"
const getDateKey = (dateStr: string) => {
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const TradingCalendar: React.FC<TradingCalendarProps> = ({ rawTrades = [], onDateClick }) => {
  const [viewDate, setViewDate] = useState(new Date());

  const monthNames = [
    'Tháng Một', 'Tháng Hai', 'Tháng Ba', 'Tháng Tư', 'Tháng Năm', 'Tháng Sáu',
    'Tháng Bảy', 'Tháng Tám', 'Tháng Chín', 'Tháng Mười', 'Tháng Mười Một', 'Tháng Mười Hai'
  ];

  const handlePrevMonth = () => setViewDate(new Date(viewDate.getFullYear(), viewDate.getMonth() - 1, 1));
  const handleNextMonth = () => setViewDate(new Date(viewDate.getFullYear(), viewDate.getMonth() + 1, 1));
  const handleToday = () => {
    const now = new Date();
    setViewDate(new Date(now.getFullYear(), now.getMonth(), 1));
  };

  const year = viewDate.getFullYear();
  const month = viewDate.getMonth();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const firstDayOfMonth = new Date(year, month, 1).getDay();
  const startOffset = firstDayOfMonth === 0 ? 6 : firstDayOfMonth - 1;
  const daysInPrevMonth = new Date(year, month, 0).getDate();

  // 1. Map lệnh theo Ngày ("YYYY-MM-DD" => RawTrade[])
  const tradesByDateMap = useMemo(() => {
    const map = new Map<string, RawTrade[]>();
    rawTrades.forEach(trade => {
      const key = getDateKey(trade.close_time || trade.open_time);
      if (key) {
        if (!map.has(key)) map.set(key, []);
        map.get(key)!.push(trade);
      }
    });
    return map;
  }, [rawTrades]);

  // 2. Thống kê dữ liệu của cả Tháng hiện tại
  const monthlyStats = useMemo(() => {
    let totalPnL = 0;
    let tradingDaysCount = 0;

    for (let day = 1; day <= daysInMonth; day++) {
      const mStr = String(month + 1).padStart(2, '0');
      const dStr = String(day).padStart(2, '0');
      const dateKey = `${year}-${mStr}-${dStr}`;
      const dayTrades = tradesByDateMap.get(dateKey) || [];

      if (dayTrades.length > 0) {
        tradingDaysCount++;
        totalPnL += dayTrades.reduce((acc, t) => {
          const gross = t.profit_gross ?? ((t.net_profit || 0) - (t.commission || 0) - (t.swap || 0));
          return acc + gross;
        }, 0);
      }
    }

    return { totalPnL, tradingDaysCount };
  }, [tradesByDateMap, year, month, daysInMonth]);

  return (
    <div className="bg-white rounded-xl shadow-premium border border-slate-100 p-6 flex flex-col">
      <div className="flex flex-col xl:flex-row xl:items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-4">
          <button
            onClick={handleToday}
            className="px-4 py-2 border border-slate-200 !text-slate-700 !text-sm font-semibold rounded-lg hover:bg-slate-50 transition-colors shadow-sm bg-white"
          >
            Hôm nay
          </button>
          <div className="flex items-center gap-3">
            <button onClick={handlePrevMonth} className="p-2 border border-slate-100 rounded bg-slate-50 !text-slate-500 hover:bg-slate-100 transition-colors">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" /></svg>
            </button>
            <h2 className="!text-lg font-bold !text-slate-900 min-w-[170px] !text-center">
              {monthNames[month]} {year}
            </h2>
            <button onClick={handleNextMonth} className="p-2 border border-slate-100 rounded bg-slate-50 !text-slate-500 hover:bg-slate-100 transition-colors">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" /></svg>
            </button>
          </div>
        </div>
        <div className="flex items-center gap-4 flex-wrap">
          <div className="!text-sm">
            <span className="!text-slate-500 font-medium mr-2">Số liệu thống kê hàng tháng:</span>
            <span className={`font-bold px-2 py-1 rounded font-mono-num ${monthlyStats.totalPnL >= 0 ? '!text-teal-500 bg-teal-50' : '!text-rose-500 bg-rose-50'}`}>
              {monthlyStats.totalPnL >= 0 ? `$${monthlyStats.totalPnL.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : `-$${Math.abs(monthlyStats.totalPnL).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
            </span>
          </div>
          <div className="bg-slate-100 border border-slate-200 !text-slate-600 !text-sm font-medium px-3 py-1.5 rounded-lg shadow-sm">
            Ngày giao dịch: <span className="font-bold !text-slate-900 ml-1">{monthlyStats.tradingDaysCount}</span>
          </div>
        </div>
      </div>

      <div className="border border-slate-200 rounded-xl overflow-hidden bg-slate-200 gap-px grid grid-cols-7">
        {['T2', 'T3', 'T4', 'T5', 'T6', 'T7', 'CN'].map(d => (
          <div key={d} className="bg-white p-3 !text-sm font-semibold !text-slate-500">{d}</div>
        ))}
        {Array.from({ length: 42 }, (_, i) => {
          const dayIndex = i - startOffset;
          const isCurrentMonth = dayIndex >= 0 && dayIndex < daysInMonth;

          let displayDay: number;
          if (dayIndex < 0) {
            displayDay = daysInPrevMonth + dayIndex + 1;
          } else if (dayIndex >= daysInMonth) {
            displayDay = dayIndex - daysInMonth + 1;
          } else {
            displayDay = dayIndex + 1;
          }

          // Lấy dữ liệu lệnh cho từng ô ô calendar
          const mStr = String(month + 1).padStart(2, '0');
          const dStr = String(displayDay).padStart(2, '0');
          const dateKey = `${year}-${mStr}-${dStr}`;

          const dayTrades = isCurrentMonth ? (tradesByDateMap.get(dateKey) || []) : [];
          const hasTrades = dayTrades.length > 0;

          const dayPnL = dayTrades.reduce((acc, t) => {
            const gross = t.profit_gross + ((t.commission || 0) + (t.swap || 0));
            return acc + gross;
          }, 0);
          const isLoss = dayPnL < 0;

          const isToday = isCurrentMonth &&
            new Date().getDate() === displayDay &&
            new Date().getMonth() === month &&
            new Date().getFullYear() === year;

          const handleDayClick = () => {
            if (!isCurrentMonth) return;
            const formattedModalDate = `Thứ ${new Date(year, month, displayDay).getDay() + 1}, ${displayDay}/${month + 1}/${year}`;
            onDateClick(formattedModalDate, dayTrades);
          };

          return (
            <div
              key={i}
              className={`bg-white p-2 min-h-[100px] xl:min-h-[120px] flex flex-col transition-colors ${isCurrentMonth ? 'cursor-pointer hover:bg-slate-50' : 'bg-slate-50/30'}`}
              onClick={handleDayClick}
            >
              {isToday ? (
                <div className="w-7 h-7 bg-blue-600 rounded-full flex items-center justify-center !text-white !text-sm font-bold mb-1 shadow-sm">
                  {displayDay}
                </div>
              ) : (
                <span className={`!text-sm font-medium ${!isCurrentMonth ? '!text-slate-300' : '!text-slate-700'}`}>
                  {displayDay}
                </span>
              )}

              {hasTrades && (
                <div className={`mt-auto ${isLoss ? 'bg-rose-50 border-rose-100' : 'bg-[#e6fcf5] border-[#c3fae8]'} rounded p-2 border`}>
                  <div className={`font-bold ${isLoss ? '!text-rose-500' : '!text-teal-600'} !text-sm font-mono-num leading-tight`}>
                    {isLoss ? `-$${Math.abs(dayPnL).toFixed(2)}` : `$${dayPnL.toFixed(2)}`}
                  </div>
                  <div className="!text-[11px] !text-slate-500 mt-1">Các giao dịch: {dayTrades.length}</div>
                </div>
              )}
            </div>
          );
        })}
      </div>
      <div className="mt-4 !text-center">
        <p className="!text-xs font-semibold !text-slate-400">Các giao dịch được thực hiện theo giờ nền tảng, có thể khác với múi giờ CE(S)T</p>
      </div>
    </div>
  );
};

export default TradingCalendar;